import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  CheckCircle2,
  Info,
  Loader2,
  Mail,
  Send,
  Trash2,
  XCircle,
} from 'lucide-react';
import {
  apiClient,
  type ApiError,
  type UpsertTenantEmailSettingsInput,
  type TenantEmailSettingsView,
} from '@/lib/api-client';
import { useAuthStore } from '@/lib/auth-store';
import { Button } from '@/components/ui/button';
import { Field, GlassSelect, TextInput } from '@/components/clients/form-primitives';

// ---------------------------------------------------------------------------
// Bloco de ajuda por provedor
// ---------------------------------------------------------------------------

const PROVIDER_HINTS = [
  {
    name: 'Gmail (conta pessoal)',
    host: 'smtp.gmail.com',
    port: 587,
    secure: false,
    note: 'Requer Senha de app com 2FA ativo. Não use a senha da conta Google.',
  },
  {
    name: 'Google Workspace',
    host: 'smtp.gmail.com',
    port: 587,
    secure: false,
    note: 'Idem Gmail. Ative "Acesso a app menos seguro" ou gere Senha de app.',
  },
  {
    name: 'Outlook / Microsoft 365',
    host: 'smtp.office365.com',
    port: 587,
    secure: false,
    note: 'Autenticação moderna (OAuth). Para SMTP básico, habilite no Admin Center.',
  },
  {
    name: 'Zoho Mail',
    host: 'smtp.zoho.com',
    port: 587,
    secure: false,
    note: 'Gere uma senha de app em Segurança da conta Zoho.',
  },
  {
    name: 'Locaweb',
    host: 'email-ssl.com.br',
    port: 465,
    secure: true,
    note: 'Porta 465 com SSL. Usuário = endereço de e-mail completo.',
  },
  {
    name: 'UOL Host',
    host: 'smtp.uolhost.com.br',
    port: 587,
    secure: false,
    note: 'Porta 587 com STARTTLS. Usuário = endereço de e-mail completo.',
  },
];

// ---------------------------------------------------------------------------
// Constantes
// ---------------------------------------------------------------------------

const PORT_OPTIONS = [
  { value: '587', label: '587 (STARTTLS, recomendado)' },
  { value: '465', label: '465 (SSL/TLS)' },
  { value: '25', label: '25 (SMTP básico, não recomendado)' },
  { value: '2525', label: '2525 (alternativo, alguns provedores)' },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDateTime(iso: string | null): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

// ---------------------------------------------------------------------------
// Estado do formulario
// ---------------------------------------------------------------------------

interface FormState {
  host: string;
  port: string;
  secure: boolean;
  username: string;
  password: string;
  fromEmail: string;
  fromName: string;
  replyTo: string;
  signature: string;
}

function emptyForm(): FormState {
  return {
    host: '',
    port: '587',
    secure: false,
    username: '',
    password: '',
    fromEmail: '',
    fromName: '',
    replyTo: '',
    signature: '',
  };
}

function formFromView(view: TenantEmailSettingsView): FormState {
  return {
    host: view.host,
    port: String(view.port),
    secure: view.secure,
    username: view.username,
    password: '', // write-only: nunca pre-preencher
    fromEmail: view.fromEmail,
    fromName: view.fromName ?? '',
    replyTo: view.replyTo ?? '',
    signature: view.signature ?? '',
  };
}

// ---------------------------------------------------------------------------
// Componente principal
// ---------------------------------------------------------------------------

export function EmailSettingsPage() {
  const qc = useQueryClient();
  const userEmail = useAuthStore((s) => s.user?.email ?? '');

  const [form, setForm] = useState<FormState>(emptyForm());
  const [testTo, setTestTo] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<{
    ok: boolean;
    message: string;
    testedAt: string;
  } | null>(null);
  const [isTesting, setIsTesting] = useState(false);
  const [showHints, setShowHints] = useState(false);

  const { data: settings, isLoading } = useQuery({
    queryKey: ['tenant-email-settings'],
    queryFn: () => apiClient.tenantEmailSettingsGet(),
    staleTime: 30_000,
    select: (data) => data ?? null,
  });

  // Popula form quando dados chegam (somente no primeiro load)
  const [formSeeded, setFormSeeded] = useState(false);
  if (settings && !formSeeded) {
    setForm(formFromView(settings));
    setTestTo(userEmail);
    setFormSeeded(true);
  }
  if (!settings && !formSeeded && userEmail) {
    setTestTo(userEmail);
    setFormSeeded(true);
  }

  const upsertMutation = useMutation({
    mutationFn: (input: UpsertTenantEmailSettingsInput) =>
      apiClient.tenantEmailSettingsUpsert(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['tenant-email-settings'] });
      setFormError(null);
    },
    onError: (e: ApiError) => {
      setFormError(e.message);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => apiClient.tenantEmailSettingsDelete(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['tenant-email-settings'] });
      setForm(emptyForm());
      setFormSeeded(false);
      setTestResult(null);
    },
    onError: (e: ApiError) => {
      setFormError(e.message);
    },
  });

  const enabledMutation = useMutation({
    mutationFn: (enabled: boolean) =>
      apiClient.tenantEmailSettingsSetEnabled({ enabled }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['tenant-email-settings'] });
    },
    onError: (e: ApiError) => {
      setFormError(e.message);
    },
  });

  function handleSave() {
    setFormError(null);
    if (!form.host.trim()) { setFormError('Informe o servidor SMTP.'); return; }
    if (!form.username.trim()) { setFormError('Informe o usuário SMTP.'); return; }
    if (!settings && !form.password) { setFormError('Informe a senha (obrigatória na primeira configuração).'); return; }
    if (!form.fromEmail.trim()) { setFormError('Informe o e-mail do remetente.'); return; }

    const input: UpsertTenantEmailSettingsInput = {
      host: form.host.trim(),
      port: parseInt(form.port, 10),
      secure: form.secure,
      username: form.username.trim(),
      fromEmail: form.fromEmail.trim(),
    };
    if (form.password) input.password = form.password;
    if (form.fromName.trim()) input.fromName = form.fromName.trim();
    if (form.replyTo.trim()) input.replyTo = form.replyTo.trim();
    if (form.signature.trim()) input.signature = form.signature.trim();

    upsertMutation.mutate(input);
  }

  async function handleTest() {
    setIsTesting(true);
    setTestResult(null);
    try {
      const res = await apiClient.tenantEmailSettingsTest(
        testTo.trim() ? { to: testTo.trim() } : undefined,
      );
      setTestResult({
        ok: res.ok,
        message: res.message,
        testedAt: new Date().toISOString(),
      });
      void qc.invalidateQueries({ queryKey: ['tenant-email-settings'] });
    } catch (e) {
      setTestResult({
        ok: false,
        message: (e as ApiError).message,
        testedAt: new Date().toISOString(),
      });
      void qc.invalidateQueries({ queryKey: ['tenant-email-settings'] });
    } finally {
      setIsTesting(false);
    }
  }

  async function handleDelete() {
    if (!window.confirm('Remover a configuração de e-mail SMTP? Esta ação não pode ser desfeita.')) return;
    deleteMutation.mutate();
  }

  function handlePortChange(value: string | null) {
    const port = value ?? '587';
    const autoSecure = port === '465';
    setForm((s) => ({ ...s, port, secure: autoSecure }));
  }

  const isSaving = upsertMutation.isPending;
  const lastTestOk = (testResult?.ok ?? null) !== null
    ? testResult!.ok
    : settings?.lastTestStatus === 'success';
  const canEnableToggle = settings !== null && settings !== undefined && lastTestOk;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12 text-sm text-[var(--fg-muted)]">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        Carregando...
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-xl font-semibold text-[var(--fg)]">E-mail de envio</h1>
        <p className="mt-1 max-w-2xl text-sm text-[var(--fg-muted)]">
          Configure o servidor SMTP do escritório para envio de e-mails
          (notificações, documentos e comunicações com clientes).
        </p>
      </div>

      {/* Feedback de erro */}
      {formError && (
        <div className="rounded-md border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-500">
          {formError}
        </div>
      )}

      {/* Card principal */}
      <div className="rounded-xl border border-[var(--border)] bg-[var(--surface)] p-6 space-y-5">

        {/* Servidor */}
        <div>
          <h2 className="mb-3 text-sm font-semibold text-[var(--fg)]">Servidor SMTP</h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="sm:col-span-2">
              <Field label="Host" required>
                <TextInput
                  value={form.host}
                  onChange={(e) => setForm((s) => ({ ...s, host: e.target.value }))}
                  placeholder="smtp.exemplo.com.br"
                  autoComplete="off"
                  disabled={isSaving}
                />
              </Field>
            </div>
            <div>
              <Field label="Porta" required>
                <GlassSelect
                  value={form.port}
                  onChange={handlePortChange}
                  options={PORT_OPTIONS}
                  placeholder="Porta"
                  disabled={isSaving}
                />
              </Field>
            </div>
          </div>

          <div className="mt-3">
            <label className="flex cursor-pointer items-center gap-2.5 text-sm text-[var(--fg)]">
              <input
                type="checkbox"
                checked={form.secure}
                onChange={(e) => setForm((s) => ({ ...s, secure: e.target.checked }))}
                disabled={isSaving}
                className="h-4 w-4 rounded border-[var(--border)] accent-[var(--accent)]"
              />
              <span>Conexão segura SSL/TLS</span>
              <span className="text-xs text-[var(--fg-muted)]">(sugerido para porta 465; desative para 587/STARTTLS)</span>
            </label>
          </div>
        </div>

        <hr className="border-[var(--border)]" />

        {/* Credenciais */}
        <div>
          <h2 className="mb-3 text-sm font-semibold text-[var(--fg)]">Credenciais</h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Usuário" required>
              <TextInput
                value={form.username}
                onChange={(e) => setForm((s) => ({ ...s, username: e.target.value }))}
                placeholder="seu@email.com.br"
                autoComplete="username"
                disabled={isSaving}
              />
            </Field>
            <Field
              label="Senha"
              required={!settings}
            >
              <TextInput
                type="password"
                value={form.password}
                onChange={(e) => setForm((s) => ({ ...s, password: e.target.value }))}
                placeholder={
                  settings?.passwordConfigured
                    ? 'Senha configurada, deixe em branco para manter'
                    : 'Senha SMTP'
                }
                autoComplete="new-password"
                disabled={isSaving}
              />
            </Field>
          </div>
          <p className="mt-2 flex items-center gap-1.5 text-xs text-[var(--fg-muted)]">
            <Info className="h-3.5 w-3.5 shrink-0" />
            Sua senha é guardada cifrada e nunca é exibida novamente.
          </p>
        </div>

        <hr className="border-[var(--border)]" />

        {/* Remetente */}
        <div>
          <h2 className="mb-3 text-sm font-semibold text-[var(--fg)]">Remetente</h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Nome do remetente">
              <TextInput
                value={form.fromName}
                onChange={(e) => setForm((s) => ({ ...s, fromName: e.target.value }))}
                placeholder="Escritório Silva e Souza"
                disabled={isSaving}
              />
            </Field>
            <Field label="E-mail do remetente" required>
              <TextInput
                value={form.fromEmail}
                onChange={(e) => setForm((s) => ({ ...s, fromEmail: e.target.value }))}
                placeholder="contato@silvaesouza.adv.br"
                autoComplete="email"
                disabled={isSaving}
              />
            </Field>
          </div>
          <div className="mt-4">
            <Field label="Reply-to (opcional)">
              <TextInput
                value={form.replyTo}
                onChange={(e) => setForm((s) => ({ ...s, replyTo: e.target.value }))}
                placeholder="respostas@silvaesouza.adv.br"
                autoComplete="off"
                disabled={isSaving}
              />
            </Field>
          </div>
          <div className="mt-4">
            <Field label="Assinatura de e-mail (opcional)">
              <textarea
                value={form.signature}
                onChange={(e) => setForm((s) => ({ ...s, signature: e.target.value }))}
                placeholder="Silva e Souza Advocacia · Tel: (51) 3000-0000"
                rows={3}
                disabled={isSaving}
                className="w-full resize-y rounded-md border border-[var(--border)] bg-[var(--surface)] px-3 py-2 text-sm text-[var(--fg)] placeholder:text-[var(--fg-muted)] focus:outline-none focus:ring-1 focus:ring-[var(--accent)] disabled:opacity-50"
              />
            </Field>
          </div>
        </div>

        <hr className="border-[var(--border)]" />

        {/* Acoes de salvar / excluir */}
        <div className="flex items-center justify-between gap-3">
          <div className="flex gap-2">
            <Button onClick={handleSave} disabled={isSaving || deleteMutation.isPending}>
              {isSaving ? (
                <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" />Salvando...</>
              ) : (
                'Salvar configuração'
              )}
            </Button>
            {settings && (
              <Button
                variant="ghost"
                onClick={() => void handleDelete()}
                disabled={isSaving || deleteMutation.isPending}
                className="text-[var(--fg-muted)] hover:text-red-500"
                title="Remover configuração de e-mail"
              >
                {deleteMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4" />
                )}
              </Button>
            )}
          </div>
          {upsertMutation.isSuccess && (
            <span className="flex items-center gap-1.5 text-sm text-emerald-500">
              <CheckCircle2 className="h-4 w-4" />
              Salvo
            </span>
          )}
        </div>
      </div>

      {/* Card: Teste de envio */}
      {settings && (
        <div className="rounded-xl border border-[var(--border)] bg-[var(--surface)] p-6 space-y-4">
          <div className="flex items-center gap-2">
            <Send className="h-4 w-4 text-[var(--fg-muted)]" />
            <h2 className="text-sm font-semibold text-[var(--fg)]">Teste de envio</h2>
          </div>

          <div className="flex items-end gap-3">
            <div className="flex-1">
              <Field label="Enviar para">
                <TextInput
                  type="email"
                  value={testTo}
                  onChange={(e) => setTestTo(e.target.value)}
                  placeholder={userEmail || 'seu@email.com'}
                  disabled={isTesting}
                />
              </Field>
            </div>
            <Button
              variant="outline"
              onClick={() => void handleTest()}
              disabled={isTesting}
            >
              {isTesting ? (
                <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" />Enviando...</>
              ) : (
                <><Mail className="mr-1.5 h-4 w-4" />Enviar e-mail de teste</>
              )}
            </Button>
          </div>

          {/* Resultado do teste atual */}
          {testResult && (
            <div
              className={`flex items-start gap-2 rounded-md border px-4 py-3 text-sm ${
                testResult.ok
                  ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400'
                  : 'border-red-500/30 bg-red-500/10 text-red-600 dark:text-red-400'
              }`}
            >
              {testResult.ok ? (
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
              ) : (
                <XCircle className="mt-0.5 h-4 w-4 shrink-0" />
              )}
              <div>
                <p className="font-medium">{testResult.ok ? 'E-mail enviado com sucesso.' : 'Falha no envio.'}</p>
                <p className="mt-0.5 text-xs">{testResult.message}</p>
                <p className="mt-0.5 text-xs opacity-70">
                  Testado em {formatDateTime(testResult.testedAt)}
                </p>
              </div>
            </div>
          )}

          {/* Historico do ultimo teste (do banco, quando nao ha resultado local) */}
          {!testResult && settings.lastTestedAt && (
            <p className="text-xs text-[var(--fg-muted)]">
              Último teste: {formatDateTime(settings.lastTestedAt)}{' '}
              {settings.lastTestStatus === 'success' ? (
                <span className="text-emerald-500">verde</span>
              ) : (
                <span className="text-red-400">
                  com falha{settings.lastTestError ? ` (${settings.lastTestError})` : ''}
                </span>
              )}
            </p>
          )}
        </div>
      )}

      {/* Card: Ativar servidor */}
      {settings && (
        <div className="rounded-xl border border-[var(--border)] bg-[var(--surface)] p-6 space-y-3">
          <h2 className="text-sm font-semibold text-[var(--fg)]">Uso nos e-mails do escritório</h2>
          <div className="flex items-start gap-3">
            <div className="pt-0.5">
              <button
                type="button"
                role="switch"
                aria-checked={settings.enabled}
                onClick={() => {
                  if (!canEnableToggle && !settings.enabled) return;
                  enabledMutation.mutate(!settings.enabled);
                }}
                disabled={
                  enabledMutation.isPending ||
                  (!canEnableToggle && !settings.enabled)
                }
                title={
                  !canEnableToggle && !settings.enabled
                    ? 'Realize um teste bem-sucedido antes de ativar.'
                    : settings.enabled
                    ? 'Desativar servidor SMTP customizado'
                    : 'Ativar servidor SMTP customizado'
                }
                className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)] disabled:cursor-not-allowed disabled:opacity-40 ${
                  settings.enabled
                    ? 'bg-[var(--accent)]'
                    : 'bg-zinc-600/40'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                    settings.enabled ? 'translate-x-5' : 'translate-x-0.5'
                  }`}
                />
              </button>
            </div>
            <div>
              <p className="text-sm text-[var(--fg)]">
                {settings.enabled
                  ? 'Servidor SMTP customizado ativo'
                  : 'Servidor SMTP customizado inativo'}
              </p>
              {!canEnableToggle && !settings.enabled ? (
                <p className="mt-0.5 text-xs text-[var(--fg-muted)]">
                  Realize um teste bem-sucedido para poder ativar este servidor.
                </p>
              ) : (
                <p className="mt-0.5 text-xs text-[var(--fg-muted)]">
                  {settings.enabled
                    ? 'Os e-mails do escritório saem pelo servidor SMTP configurado acima.'
                    : 'Os e-mails saem pelo servidor padrão do VoxisAdv.'}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Bloco de ajuda por provedor */}
      <div className="rounded-xl border border-[var(--border)] bg-[var(--surface)] p-6 space-y-3">
        <button
          type="button"
          onClick={() => setShowHints((v) => !v)}
          className="flex w-full items-center justify-between text-sm font-semibold text-[var(--fg)] hover:text-[var(--accent)] transition-colors"
        >
          <span className="flex items-center gap-2">
            <Info className="h-4 w-4 text-[var(--fg-muted)]" />
            Configurações por provedor de e-mail
          </span>
          <span className="text-xs text-[var(--fg-muted)]">{showHints ? 'Fechar' : 'Ver'}</span>
        </button>

        {showHints && (
          <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
            {PROVIDER_HINTS.map((h) => (
              <div
                key={h.name}
                className="rounded-lg border border-[var(--border)] bg-[var(--surface-2,var(--surface))] p-3 text-xs space-y-1"
              >
                <p className="font-semibold text-[var(--fg)]">{h.name}</p>
                <p className="font-mono text-[var(--fg-muted)]">
                  {h.host}:{h.port}{h.secure ? ' (SSL)' : ' (STARTTLS)'}
                </p>
                <p className="text-[var(--fg-muted)]">{h.note}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
